import {
  IonButton,
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import useEnhancedAuth from '../EnhancedAuth';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { returnToMain } from '../utils/nav';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import UrpassNavBar from '../usecases/urpass/UrpassNavBar';
import {
  apiUrpassActivateTicket,
  apiUrpassGetAgencies,
  apiUrpassGetTickets,
} from '../services/urpass';
import UrpassInfo from '../usecases/urpass/UrpassInfo';
import UrpassTickets from '../usecases/urpass/UrpassTickets';
import { LoadingController } from '../utils/LoadingController';
import { UrpassTicketType } from '../services/types/urpassapitypes';
import { storageSetUrpassTenants } from '../utils/storage';
import { getAppError, getAppSuccess } from '../utils/messages';
import { useToaster } from '../hooks/useToaster';
import './UrpassPage.css';

const UrpassPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const router = useHistory();
  const [t] = useTranslation('global');
  const [present] = useToaster();

  useEffect(() => {
    const storeTenants = async () => {
      try {
        const tenants = await apiUrpassGetAgencies(auth);
        storageSetUrpassTenants(tenants.data);
      } catch (error) {
        console.log('Error fetching and storing Urpass tenants', error);
      }
    };
    storeTenants();
  }, [auth]);

  const [availableTickets, setAvailableTickets] = useState<
    UrpassTicketType[] | undefined
  >(undefined);
  const [activeTickets, setActiveTickets] = useState<
    UrpassTicketType[] | undefined
  >(undefined);
  const [expiredTickets, setExpiredTickets] = useState<
    UrpassTicketType[] | undefined
  >(undefined);

  const loadDataFromApi = async () => {
    LoadingController.show();
    try {
      const response = await apiUrpassGetTickets(auth);
      setAvailableTickets(
        response.data.filter(
          (ticket: UrpassTicketType) =>
            ticket.status.toLowerCase() === 'inactive',
        ),
      );
      setExpiredTickets(
        response.data.filter(
          (ticket: UrpassTicketType) =>
            ticket.status.toLowerCase() === 'expired',
        ),
      );
      setActiveTickets(
        response.data.filter(
          (ticket: UrpassTicketType) =>
            ticket.status.toLowerCase() === 'active',
        ),
      );
    } catch (e) {
      console.log('Error occurred while retrieving data', e);
    } finally {
      LoadingController.hide();
    }
  };

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  // dont use useEffect, it does not trigger the second time a page with the same parameter is loaded.
  // we want the page to reload the current data every time it is opened
  useIonViewWillEnter(() => {
    loadDataFromApi();
  }, [auth.isLoading]);

  const refresh = (e: CustomEvent) => {
    loadDataFromApi().then(() => e.detail.complete());
  };

  const addPass = () => {
    router.push('/urpassadd');
  };

  const activateTicket = async (ticket: UrpassTicketType) => {
    LoadingController.show();
    try {
      await apiUrpassActivateTicket(auth, ticket.urpassTenantId, ticket.id);
      present(getAppSuccess(t('urpass.ticketactivated')));
    } catch (error: { message?: string } | any) {
      present(getAppError(error?.message || t('urpass.ticketactivationerror')));
    } finally {
      loadDataFromApi();
      LoadingController.hide();
    }
  };

  const viewTicket = (ticket: UrpassTicketType) => {
    router.push('/urpassview/' + ticket.urpassTenantId + '/' + ticket.id);
  };

  return (
    <IonPage id="urpass-page">
      <>
        <AppPageHeader
          isPageActive={isPageActive}
          back={() => returnToMain(router)}
          title={t('urpass.title')}
        ></AppPageHeader>
        <IonContent fullscreen className="ion-padding default-content">
          <IonRefresher slot="fixed" onIonRefresh={refresh}>
            <IonRefresherContent></IonRefresherContent>
          </IonRefresher>
          <UrpassNavBar />
          <UrpassInfo />
          <UrpassTickets
            availableTickets={availableTickets}
            expiredTickets={expiredTickets}
            activeTickets={activeTickets}
            activateTicket={activateTicket}
            viewTicket={viewTicket}
          />
          <div className="pt-3 ion-text-center">
            <IonButton
              className="btn-default btn-wide-80"
              onClick={() => addPass()}
            >
              {t('urpass.addpass')}
            </IonButton>
          </div>
          <div className="pt-5 ion-text-center">
            <a
              className="urpass-infolink"
              href={t('urpass.infourl')!}
              rel={'noreferrer'}
              target={'_blank'}
            >
              {t('urpass.infohint')}
            </a>
          </div>
        </IonContent>
      </>
    </IonPage>
  );
};

export default UrpassPage;
